/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import useMediaQuery from '../../hooks/useMediaQuery';
import ExternalLink from '../ExternalLink';
import Link from '../Link';
import Text from '../Text';
import useStyles from './styles';
import {
  closeButton,
  videoContainer,
  videoOverlay,
  videoWrapper,
} from './styles';

const FAProgrammes = ({
  heading,
  description,
  descriptionLine,
  descriptionFirstLine,
  descriptionSecondLine,
  images,
  indexVal,
  ctaId,
  videos,
  footer,
  href,
}) => {
  const styles = useStyles();
  const [showVideo, setShowVideo] = useState(false);
  const descriptionRef = useRef(null);
  const [showExpandLink, setShowExpandLink] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [linkHovered, setLinkHovered] = useState(false);
  const [linkTouched, setLinkTouched] = useState(false);

  const isMobile = useMediaQuery(
    'screen and (min-width: 760px) and (max-width: 996px)'
  );
  const isSmallMobile = useMediaQuery('screen and (max-width: 760px)');

  const disableScrolling = () => {
    document.documentElement.style.overflow = 'hidden';
  };

  const enableScrolling = () => {
    document.documentElement.style.overflow = '';
  };

  const handleClick = () => {
    setShowVideo(true);
    disableScrolling();
  };

  const handleCloseVideo = e => {
    e.stopPropagation();
    setShowVideo(false);
    enableScrolling();
  };

  const handleKeyDown = (e, action) => {
    switch (e.key) {
      case 'Enter':
        if (e.key === 'Enter') {
          e.preventDefault();
        }
        if (action === 'buttonClick') {
          handleExpandClick(e);
        }
        if (action === 'buttonCollapseClick') {
          handleCollapseClick(e);
        }
        if (action === 'open') {
          handleClick();
        } else if (action === 'close') {
          handleCloseVideo(e);
        }
        break;

      default:
        break;
    }
  };
  const extractTextFromJSX = jsx => {
    if (typeof jsx === 'string') {
      return jsx;
    }
    if (Array.isArray(jsx)) {
      return jsx.map(extractTextFromJSX).join('');
    }
    if (jsx && typeof jsx === 'object' && jsx.props) {
      return extractTextFromJSX(jsx.props.children);
    }
    return '';
  };

  const plainTextDescription = extractTextFromJSX(descriptionSecondLine);
  const otherDescription = extractTextFromJSX(description);
  const JustPlayDescription = extractTextFromJSX(descriptionLine);

  const renderDescriptionBold = des => {
    const phrases = ['Just Play'];

    phrases.forEach(phrase => {
      const regex = new RegExp(phrase);
      des = des.replace(regex, `<b>${phrase}</b>`);
    });

    return (
      <>
        <span
          dangerouslySetInnerHTML={{
            __html: des,
          }}
        ></span>
      </>
    );
  };

  useEffect(() => {
      if (!descriptionRef.current) return;

      let rafID;
      const element = descriptionRef.current;
      if(!element) return;

      rafID = requestAnimationFrame(()=>{
      const lineHeight = parseFloat(
        window.getComputedStyle(element).lineHeight
      );
      const scrollHeight = element.scrollHeight;
      const lines = Math.ceil(scrollHeight / lineHeight);
      setShowExpandLink(() => {
        const shouldShow =
          lines > 4 && descriptionLine
            ? true
            : lines > 0 && descriptionSecondLine
            ? true
            : lines > 6 && description
            ? true
            : false;
        return shouldShow;
      });
   })
   return () => cancelAnimationFrame(rafID); 
  }, [descriptionLine, descriptionSecondLine, description]);

  const handleExpandClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(true);
  };

  const handleCollapseClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(false);
  };

  return (
    <div css={styles.waysContainer}>
      <div css={styles.waysToPlaycards} id={ctaId}>
        <div css={styles.innerWrapper}>
          <div css={styles.card}>
            <Link
              css={styles.imgSection(images, indexVal)}
              id={ctaId}
              tabIndex={0}
              onClick={handleClick}
              onKeyDown={e => handleKeyDown(e, 'open')}
            >
              {showVideo && (
                <div
                  css={videoOverlay}
                  onClick={handleCloseVideo}
                  onKeyDown={e => handleKeyDown(e, 'close')}
                  role="button"
                >
                  <div
                    css={videoContainer}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                    role="button"
                  >
                    <div css={videoWrapper}>
                      <iframe
                        width="100%"
                        height="100%"
                        src={videos}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="video"
                      ></iframe>
                      <span
                        onClick={handleCloseVideo}
                        onKeyDown={e => handleKeyDown(e, 'close')}
                        role="button"
                        tabIndex={0}
                      >
                        <img
                          css={closeButton}
                          src="/images/desktop/home/closeLightboxVideo.svg"
                          alt="Close Video"
                        />
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {!showVideo && (
                <div css={styles.imageWrapper}>
                  <span
                    onClick={handleClick}
                    onKeyDown={e => handleKeyDown(e, 'open')}
                    role="button"
                  >
                    <img
                      src="/images/desktop/home/videoPlay.svg"
                      alt="Play Video"
                      className="youtube"
                    />
                  </span>
                </div>
              )}
            </Link>

            <div css={styles.infoSection}>
              <div css={styles.cardalign(isExpanded)}>
                <Text
                  as="p"
                  css={styles.header}
                  onClick={handleClick}
                  role="link"
                  onKeyDown={e => handleKeyDown(e, 'open')}
                >
                  {heading}
                </Text>
                <Text
                  css={styles.description(isExpanded)}
                  ref={descriptionRef}
                  id={`description-section-${ctaId}`}
                  role="link"
                  onClick={handleClick}
                  onKeyDown={e => handleKeyDown(e, 'open')}
                >
                  {showExpandLink && (
                    <>
                      {!isExpanded ? (
                        <span>
                          {descriptionSecondLine ? (
                            <span>
                              {descriptionFirstLine}
                              {isMobile || isSmallMobile ? (
                                <>{plainTextDescription.slice(0, 18)}... </>
                              ) : (
                                <>{plainTextDescription.slice(0, 23)}... </>
                              )}
                            </span>
                          ) : (
                            <span>
                              {isMobile || isSmallMobile ? (
                                <>
                                  {' '}
                                  {renderDescriptionBold(
                                    JustPlayDescription.slice(0, 163)
                                  )}
                                  ...{' '}
                                </>
                              ) : (
                                <>
                                  {renderDescriptionBold(
                                    JustPlayDescription.slice(0, 177)
                                  )}
                                  ...{' '}
                                </>
                              )}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span>
                          {descriptionLine ? (
                            <>{descriptionLine}</>
                          ) : (
                            <>
                              {descriptionFirstLine}
                              {descriptionSecondLine}
                            </>
                          )}{' '}
                        </span>
                      )}

                      <span
                        onClick={
                          !isExpanded ? handleExpandClick : handleCollapseClick
                        }
                        tabIndex={0}
                        role="button"
                        css={styles.showMore}
                        onKeyDown={e =>
                          !isExpanded
                            ? handleKeyDown(e, 'buttonClick')
                            : handleKeyDown(e, 'buttonCollapseClick')
                        }
                      >
                        {!isExpanded ? 'show more' : 'show less'}
                        <span css={styles.expandArrow}>
                          <img
                            src={
                              !isExpanded
                                ? '/images/desktop/home/disabilityFootball/downArrow.svg'
                                : '/images/desktop/home/disabilityFootball/upArrow.svg'
                            }
                            alt="/images/desktop/home/disabilityFootball/downArrow.svg"
                          />
                        </span>
                      </span>
                    </>
                  )}
                  <div> {!showExpandLink && <>{otherDescription}</>}</div>
                </Text>
                <ExternalLink
                  css={styles.link}
                  href={href}
                  onMouseEnter={() => setLinkHovered(true)}
                  onMouseLeave={() => setLinkHovered(false)}
                  onTouchStart={() => setLinkTouched(true)}
                  onTouchEnd={() => setLinkTouched(false)}
                >
                  <Text css={styles.learnMore}>
                    <span css={styles.learnmore}>{footer}</span>
                    <img
                      css={styles.learnmoreArrow}
                      src={
                        linkHovered || linkTouched
                          ? '/images/desktop/home/rightArrowBlue.svg'
                          : '/images/desktop/home/rightArrow.svg'
                      }
                      alt="/images/desktop/home/rightArrow.svg"
                    />{' '}
                  </Text>
                </ExternalLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

FAProgrammes.propTypes = {
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  descriptionLine: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  descriptionFirstLine: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  descriptionSecondLine: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  images: PropTypes.object,
  indexVal: PropTypes.any,
  ctaId: PropTypes.string,
  videos: PropTypes.any,
  footer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default FAProgrammes;
